import {
	type AcceptedEffortUpgrade,
	useUpgradableEffort,
} from "@ui/organisms/QuestionBox/useUpgradableEffort";
import {
	createEffect,
	createSignal,
	onCleanup,
	onMount,
	untrack,
} from "solid-js";
import { checkPublicAskPageQuestion } from "@/client";
import { clearClipboard } from "~/modules/shared/chat/components/clipboard/clipboardStore";
import { signInUrl } from "~/modules/shared/utils/auth";
import {
	askPage402UserMessage,
	askPageValidationMessage,
} from "../askPageAskErrors";
import { stashQuestionForHandoff } from "../askPageQuestionHandoff";
import { postAskPageQuestion } from "../postAskPageQuestion";

export type UseAskPageFormOptions = {
	handle: string;
	isSignedIn: boolean;
	/** Set when replying from within an existing thread (Task 4.1d). */
	threadRootId?: string;
	/** Set from a clicked starter-question suggestion (public ask-page redesign). */
	prefill?: string;
	/**
	 * Set (alongside `prefill`) when this text was restored from the
	 * sign-in stash rather than a starter-question click - the visitor
	 * already tried to submit it once before hitting the sign-in-required
	 * paywall, so it re-submits automatically rather than waiting for
	 * another click.
	 */
	autoSubmit?: boolean;
	/**
	 * Given, a created question is handed here instead of being navigated to,
	 * so a page that can show it in place doesn't reload.
	 */
	onAsked?: (asked: AskedQuestion) => void;
	/** The latest effort upgrade accepted in the thread being replied to. */
	acceptedEffortUpgrade?: AcceptedEffortUpgrade;
};

export type AskedQuestion = {
	id: string;
	path: string;
	/** The question as typed, mentions in their readable form. */
	question: string;
};

export type AskPageFormStatus = "idle" | "submitting";

/** `availableAt` is only ever set when `canAsk` is false, and may be unknown even then. */
type GuestAllowance = { canAsk: boolean; availableAt: Date | null };

/** setTimeout fires immediately for any delay above this. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/** Refused only when the server says the visitor can't ask; a failed check lets the ask decide. */
async function guestAllowance(handle: string): Promise<GuestAllowance> {
	try {
		const { response } = await checkPublicAskPageQuestion({
			path: { handle },
		});
		if (response?.status !== 429) {
			return { canAsk: true, availableAt: null };
		}
		const seconds = Number(response.headers?.get("Retry-After"));
		return {
			canAsk: false,
			availableAt:
				Number.isFinite(seconds) && seconds > 0
					? new Date(Date.now() + seconds * 1000)
					: null,
		};
	} catch {
		return { canAsk: true, availableAt: null };
	}
}

function isSameDay(a: Date, b: Date): boolean {
	return a.toDateString() === b.toDateString();
}

function guestLimitMessage(availableAt: Date | null): string {
	const prefix =
		"You've reached the limit for guest questions. Please sign up or come back";
	if (availableAt === null) {
		return `${prefix} later.`;
	}

	// Rounded up, so the time shown is never one at which the guest would
	// still be refused.
	const at = new Date(Math.ceil(availableAt.getTime() / 60_000) * 60_000);
	const time = new Intl.DateTimeFormat(undefined, {
		hour: "numeric",
		minute: "2-digit",
	}).format(at);
	const now = new Date();
	const tomorrow = new Date(now);
	tomorrow.setDate(now.getDate() + 1);

	if (isSameDay(at, now)) {
		return `${prefix} at ${time}.`;
	}
	if (isSameDay(at, tomorrow)) {
		return `${prefix} tomorrow at ${time}.`;
	}
	const day = new Intl.DateTimeFormat(undefined, {
		weekday: "long",
		day: "numeric",
		month: "long",
	}).format(at);
	return `${prefix} on ${day} at ${time}.`;
}

export function useAskPageForm(props: UseAskPageFormOptions) {
	const [prompt, setPrompt] = createSignal(props.prefill ?? "");
	const { effort, chooseEffort, rememberEffort } = useUpgradableEffort(
		() => props.acceptedEffortUpgrade,
	);
	const [status, setStatus] = createSignal<AskPageFormStatus>("idle");
	const [error, setError] = createSignal<string | null>(null);
	const [guestLimit, setGuestLimit] = createSignal<
		{ availableAt: Date | null } | undefined
	>();
	let recheckTimer: ReturnType<typeof setTimeout> | undefined;
	// Guards against firing more than once if the effect below re-runs.
	let autoSubmitted = false;

	/** Returns whether the guest may ask; a guest who may not gets the box back once their window resets. */
	const applyGuestAllowance = (allowance: GuestAllowance): boolean => {
		clearTimeout(recheckTimer);
		if (allowance.canAsk) {
			setGuestLimit(undefined);
			return true;
		}
		setGuestLimit({ availableAt: allowance.availableAt });
		if (allowance.availableAt !== null) {
			recheckTimer = setTimeout(
				() => void checkGuestAllowance(),
				Math.min(allowance.availableAt.getTime() - Date.now(), MAX_TIMEOUT_MS),
			);
		}
		return false;
	};

	const checkGuestAllowance = async () => {
		if (!props.isSignedIn) {
			applyGuestAllowance(await guestAllowance(props.handle));
		}
	};

	onMount(() => void checkGuestAllowance());
	onCleanup(() => clearTimeout(recheckTimer));

	/**
	 * `boxPrompt` is the prompt as the question box submits it, with its
	 * mentions intact: as the box edits it, `prompt()` holds only their
	 * readable form. Without one, `prompt()` is a prefill being auto-submitted.
	 */
	const submit = async (boxPrompt?: string) => {
		const trimmed = (boxPrompt ?? prompt()).trim();
		if (trimmed === "" || status() === "submitting") {
			return;
		}

		setStatus("submitting");
		setError(null);
		if (props.threadRootId) {
			clearClipboard();
		}

		const result = await postAskPageQuestion({
			handle: props.handle,
			prompt: trimmed,
			threadRootId: props.threadRootId,
			effort: effort(),
		});

		if (result.ok === true && props.onAsked) {
			const question = prompt().trim() || trimmed;
			setStatus("idle");
			setPrompt("");
			props.onAsked({ id: result.id, path: result.path, question });
			// Shown in place, so the page doesn't reload and check on mount;
			// this question may have been the guest's last.
			void checkGuestAllowance();
			return;
		}

		if (result.ok === true) {
			window.location.href = result.path;
			return;
		}

		// A dropped connection leaves httpStatus unset, and lands on the
		// generic error message below.
		const httpStatus = result.status;
		const apiError = result;

		if (
			httpStatus === 429 &&
			!props.isSignedIn &&
			!applyGuestAllowance(await guestAllowance(props.handle))
		) {
			setStatus("idle");
			return;
		}

		setStatus("idle");
		if (httpStatus === 429 && apiError?.message && !props.isSignedIn) {
			setError(apiError.message);
		} else if (httpStatus === 401 && props.threadRootId) {
			setError("Sign in to reply in this thread.");
		} else if (httpStatus === 401) {
			// A fresh top-level ask on a paid page, anonymous - stash the
			// typed question so it survives the sign-in round trip; ask.tsx's
			// onMount restores it and resubmits automatically on return.
			stashQuestionForHandoff(props.handle, trimmed);
			window.location.href = signInUrl();
		} else if (httpStatus === 402) {
			const { message, stashForEntitlementPurchase } =
				askPage402UserMessage(apiError);
			if (stashForEntitlementPurchase) {
				// Re-stash so the question survives a pass/subscription purchase;
				// ask.tsx's onMount resubmits after reload.
				stashQuestionForHandoff(props.handle, trimmed);
			}
			setError(message);
		} else if ((httpStatus === 403 || httpStatus === 409) && apiError?.message) {
			setError(apiError.message);
		} else if (httpStatus === 422) {
			setError(
				askPageValidationMessage(apiError, "prompt") ??
					askPageValidationMessage(apiError, "effort") ??
					apiError?.message ??
					"That question could not be sent. Check what you typed and try again.",
			);
		} else {
			// Any other 429 lands here too. Signed-in visitors aren't rate
			// limited on this endpoint (credits bound them), so a 429 we can't
			// explain is either from the edge, with no message, or the guest
			// cap refusing someone whose session expired after the page
			// loaded. Neither is a limit we can describe, so don't name one.
			setError("Something went wrong asking that question. Please try again.");
		}
	};

	createEffect(() => {
		if (props.prefill !== undefined) {
			setPrompt(props.prefill);
		}
		// untrack: submit() itself reads prompt()/status() before its first
		// await, which - without untrack - would make this effect an implicit
		// dependent of both, re-running (and risking a second auto-submit,
		// or clobbering a since-edited prompt back to the stale prefill) on
		// any later prompt/status change, not just a prefill/autoSubmit change.
		if (props.autoSubmit && props.prefill && !autoSubmitted) {
			autoSubmitted = true;
			untrack(() => void submit());
		}
	});

	return {
		prompt,
		setPrompt,
		effort,
		setEffort: chooseEffort,
		rememberEffort,
		status,
		error,
		disabledReason: () => {
			const limit = guestLimit();
			return limit ? guestLimitMessage(limit.availableAt) : undefined;
		},
		submit,
	};
}
